/**
 * navConfig.js — single source of truth for role-aware navigation items.
 *
 * Each item declares an `allow` array of roles that may see it. Sections that
 * are NOT listed here are NOT filtered (Monitor, Reportes, Control, Dashboard
 * are always visible — `RoleRoute` still gates the actual pages).
 *
 * Item shapes are kept minimal and superset-compatible: existing nav components
 * (TopNav, AdminPage, BottomNav, ProfilePage) extend these with their own
 * presentation fields (icons, descriptions, colors). Identifiers (`id`, `href`,
 * `title`) match the values already wired into the router and the nav
 * components — DO NOT introduce items that don't exist on disk.
 *
 * Optional presentation fields (used by AdminPage mobile cards):
 *   - description : human-friendly subtitle
 *   - iconColor   : Tailwind text-* class for the icon
 *   - iconBg      : Tailwind bg-* class for the icon container
 *
 * TopNav and BottomNav only consume id/title/href/icon/allow — extra fields are
 * harmless when ignored.
 */
import { ROLES } from "./roles.js";

const { MASTER, OWNER, MEMBER } = ROLES;

// ─── Badges — un contador, varios sitios donde se dibuja ─────────────────────
//
// Un destino declara `badge: "<clave>"` y la pantalla que lo pinta resuelve esa
// clave contra sus contadores en vivo. Los padres (el trigger "Reportes" del
// escritorio, la pestaña "Reportes" del móvil) NO tienen número propio: suman
// el de sus hijos con `sumGroupBadges`, así el globo de arriba y el de adentro
// no pueden divergir y siempre hay, al abrir, un destino que explica el número.

export const NAV_BADGE_LABELS = Object.freeze({
  alerts: "alertas sin leer",
  pending: "pendientes",
});

/**
 * Contador en vivo de un destino. Sin `badge` (o sin número para esa clave) → 0.
 *
 * @param {string|undefined} badge
 * @param {Record<string, number>} counts
 * @returns {number}
 */
export function badgeValue(badge, counts = {}) {
  return badge ? (counts[badge] ?? 0) : 0;
}

/**
 * Suma los contadores de todos los destinos de un árbol agrupado.
 *
 * @param {Array<{items: Array<{badge?: string}>}>} groups
 * @param {Record<string, number>} counts
 * @returns {number}
 */
export function sumGroupBadges(groups, counts) {
  return groups.reduce(
    (total, group) =>
      total +
      group.items.reduce((n, item) => n + badgeValue(item.badge, counts), 0),
    0,
  );
}

// ─── Reportes — mega-menú de escritorio + pantalla /reportes en móvil ────────
//
// Un solo árbol para las dos superficies. Estaba escrito dos veces (TopNav y
// ReportesPage) y por eso el globo de alertas sin leer llegó a una sola: el
// desplegable lo mostraba en el padre y la pantalla móvil no lo mostraba en
// ninguna parte.
//
// `description` la usa SOLO la pantalla móvil, donde cada destino es una
// tarjeta; el mega-menú de escritorio muestra el título pelado (una columna de
// 150px no da para dos líneas de ayuda por ítem).

export const REPORTES_GROUPS = [
  {
    label: "Operación",
    icon: "directions_car",
    items: [
      {
        title: "Recorridos",
        href: "/reportes/recorridos",
        icon: "route",
        description: "Movimiento, paradas y kilómetros en un período",
      },
    ],
  },
  {
    label: "Seguridad",
    icon: "shield",
    items: [
      {
        title: "Excesos de velocidad",
        href: "/reportes/velocidad",
        icon: "speed",
        description: "Eventos sobre el límite configurado",
      },
      {
        title: "Historial de alertas",
        href: "/analisis",
        icon: "history",
        description: "Registro completo de alertas del sistema",
        // Las alertas sin leer son de ESTE destino: AlertsPage llama a
        // clearUnread() al montar, así que entrar acá es lo que apaga el globo.
        badge: "alerts",
      },
    ],
  },
];

// ─── Gestión section (legacy — only the wizard entry survives PR-B) ──────────
//
// PR-B consolidated all admin surfaces under /admin (sidebar in
// `lib/adminNav.js`). The legacy /gestion/* paths now redirect to /admin/*
// (see App.jsx commit 7). The single survivor here is the wizard quick-link
// because we keep using it via the BottomNav badge counter and the legacy
// `wizard-dispositivo` redirect: `/gestion/dispositivos/wizard` →
// `/admin/flota/dispositivos/wizard`.
//
// Treat ALL_GESTION_ITEMS as deprecated; new admin surfaces should be
// registered in lib/adminNav.js.

export const ALL_GESTION_ITEMS = [
  {
    id: "wizard-dispositivo",
    title: "Agregar dispositivo",
    href: "/gestion/dispositivos/wizard",
    icon: "auto_fix_high",
    description: "Asistente paso a paso para registrar un GPS nuevo",
    iconColor: "text-info",
    iconBg: "bg-info/10",
    allow: [MASTER, OWNER],
  },
];

/**
 * Filter Gestión items for the given role string. `null`/`undefined` returns [].
 *
 * @param {string|null|undefined} role
 * @returns {Array<{id:string,title:string,href:string,icon:string,allow:string[]}>}
 */
export function buildGestionItems(role) {
  if (!role) return [];
  return ALL_GESTION_ITEMS.filter((item) => item.allow.includes(role));
}

// ─── Bottom nav (mobile) ──────────────────────────────────────────────────────
//
// Mirrored from frontend/src/components/BottomNav.jsx (NAV_ITEMS).
//
// Allow rules:
//   - Dashboard / Administración → fleet managers (master+operator)
//   - Monitor / Reportes         → everyone (viewer's primary surfaces)
//   - Perfil                     → viewer only (TopNav has the avatar
//                                  dropdown for the rest, viewer has no
//                                  TopNav avatar surface on mobile)
//
// Viewer ends up with 3 items (Monitor + Reportes + Perfil); the other roles
// keep 4 (Dashboard, Monitor, Reportes, Administración).

export const ALL_BOTTOM_NAV_ITEMS = [
  {
    id: "dashboard",
    path: "/dashboard",
    label: "Dashboard",
    icon: "home",
    allow: [MASTER, OWNER],
  },
  {
    id: "monitor",
    path: "/",
    label: "Monitor",
    icon: "map",
    end: true,
    allow: [MASTER, OWNER, MEMBER],
  },
  // "Control" se fue: sus tres destinos son ahora una sección del rail de
  // /admin, al que ya lleva la pestaña "Administración". Dos pestañas que
  // desembocan en el mismo shell no son dos sitios.
  {
    id: "reportes",
    path: "/reportes",
    label: "Reportes",
    icon: "bar_chart",
    // La rama "alerts" de BottomNav existía desde siempre y ningún ítem la
    // declaraba: en el teléfono el contador de alertas no se veía en ninguna
    // pantalla.
    //
    // OJO: acá se declara UNA clave, mientras el trigger de escritorio deriva
    // su número sumando el árbol (`sumGroupBadges(REPORTES_GROUPS)`). Los dos
    // padres coinciden porque "alerts" es hoy la única clave con badge en ese
    // árbol. Si mañana un segundo destino de Reportes declara otra clave, esta
    // pestaña tiene que pasar a derivarse igual que el trigger o los dos padres
    // mostrarán números distintos. Lo atajan dos tests de
    // lib/__tests__/navConfig.test.js: "el trigger de escritorio y la pestaña
    // del móvil dan el mismo número" y "el árbol de Reportes solo declara
    // claves que las tres superficies alimentan".
    badge: "alerts",
    allow: [MASTER, OWNER, MEMBER],
  },
  {
    id: "gestion",
    path: "/admin",
    // PR-B (review MEDIUM-5): full "Administración" label per spec. The
    // bottom-nav already applies `truncate` + `max-w-full` to the rendered
    // label, and BottomNav now adds a `title` attribute for the tooltip when
    // the truncate kicks in on narrow viewports.
    label: "Administración",
    icon: "folder_managed",
    badge: "pending",
    allow: [MASTER, OWNER],
  },
  {
    id: "perfil",
    path: "/perfil",
    label: "Perfil",
    icon: "person",
    allow: [MEMBER],
  },
];

/**
 * Filter bottom-nav items for the given role string. `null`/`undefined` returns [].
 */
export function buildBottomNavItems(role) {
  if (!role) return [];
  return ALL_BOTTOM_NAV_ITEMS.filter((item) => item.allow.includes(role));
}

// PR-B.5 promoted ProfilePage's left-rail tabs to real /perfil/* routes; the
// sidebar tree now lives in `lib/profileNav.js`. The legacy
// ALL_PROFILE_NAV_ITEMS + buildProfileNavItems exports were removed.

// ─── Post-login destination ───────────────────────────────────────────────────

/**
 * Compute the route to navigate to immediately after a successful login.
 *
 * Viewers land on the live map (`/`) — they have no dashboard. Everyone else
 * goes to `/dashboard`. Pure function so it can be unit-tested without
 * mounting the LoginPage component.
 *
 * @param {{role?: string}|null|undefined} user
 * @returns {string}
 */
export function getPostLoginDestination(user) {
  return user?.role === MEMBER ? "/" : "/dashboard";
}
